/**
 * The read-only detail view of a COMMITTED pack, down to the rule.
 *
 * Why it exists: the overview linked drafts only. A committed pack — Egg,
 * Meilen, Uster, Zollikon — showed a badge that said "verifiziert" and, two
 * lines below it, a coverage line that said "4 offen", and there was nowhere to
 * go to find out which four. Since 2026-09-16 this page named the LAYER that
 * held them; since 2026-09-17 (Founder decision) it names the rule.
 *
 * Two requests, one source of truth. The header and the coverage card stand on
 * the register entry the caller already has (`GET /api/v1/gemeinden`), the
 * zones and the rules on `GET /api/v1/gemeinden/{kanton}/{gemeinde}`
 * (app/rulebase_index.py `committed_pack_detail`). Both are built from the
 * same files by the same module, and a backend test pins that the counts of
 * the one are the counts of the other — so the badge above and the list below
 * cannot disagree.
 *
 * What this page never does: evaluate a rule. `check_summary` is the formula
 * TEXT as it stands in the pack, shown in monospace so a reader can compare it
 * with the BZO; the engine (`engine/baupilot.py`) is the only thing that ever
 * computes with it, in a run, against a dossier. And nothing here writes: a
 * committed pack changes by a reviewed diff in git, which the page says.
 *
 * Unverified rows and rules keep the indicative treatment used everywhere
 * else in the product — dashed border, grey badge with a text label, and a
 * sentence — so an open rule is never presented as the equal of a verified one.
 */
import { ArrowLeft, FileText, GitBranch, ShieldCheck } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useI18n, type Translate } from '@/i18n'
import { buildPath, navigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'
import { formatNumber } from '@/lib/utils'

import { CoverageMeter } from './CoverageMeter'
import { OpenCountBadge } from './DraftBadge'
import { formatValue, openCount, packIdOf, splitPackId } from './rulebase'
import type {
  CommittedLayerRules,
  CommittedPackDetail as CommittedPackDetailBody,
  CommittedRule,
  CommittedZoneRow,
  GemeindePack,
} from '@/api/types'

export function CommittedPackDetail({
  pack,
  workspaceId,
}: {
  pack: GemeindePack
  workspaceId: string
}) {
  const { t } = useI18n()
  const packId = packIdOf(pack)
  const { kanton, gemeinde } = splitPackId(packId)
  const rules = pack.rules_effective
  const sources = pack.sources ?? []
  const open = openCount(rules) + openCount(pack.zones)

  const detail = useAsync<CommittedPackDetailBody>(
    (signal) => api.committedPack(kanton, gemeinde, signal),
    [kanton, gemeinde],
  )

  return (
    <div className="flex flex-col gap-8" data-testid="committed-pack" data-pack={packId}>
      <section className="flex flex-col gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => navigate(buildPath('rulebase', { workspaceId }))}
        >
          <Icon icon={ArrowLeft} />
          {t('common.back')}
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-[32px] leading-tight font-semibold">
            {pack.gemeinde} <span className="text-muted-2">({pack.kanton})</span>
          </h1>
          <Badge variant="info" data-testid="verified-badge-count">
            <Icon icon={ShieldCheck} />
            {t('rulebase.verifiedBadgeCount', { verified: rules.verified, total: rules.total ?? 0 })}
          </Badge>
          {open > 0 ? <OpenCountBadge count={open} /> : null}
        </div>
        <code className="num text-[13px] text-muted-2">{packId}</code>
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-muted">
          {pack.regime ? (
            <div className="flex gap-1.5">
              <dt>{t('coverage.regime')}:</dt>
              <dd className="num">{pack.regime}</dd>
            </div>
          ) : null}
          {detail.data?.hoehenregime ? (
            <div className="flex gap-1.5">
              <dt>{t('packDetail.hoehenregime')}:</dt>
              <dd className="num" data-testid="pack-hoehenregime">
                {detail.data.hoehenregime}
              </dd>
            </div>
          ) : null}
          {detail.data?.bzo_version ? (
            <div className="flex gap-1.5">
              <dt>{t('packDetail.bzoVersion')}:</dt>
              <dd data-testid="pack-bzo-version">{detail.data.bzo_version}</dd>
            </div>
          ) : null}
        </dl>
        <p className="max-w-[70ch] text-[15px] text-muted">{t('packDetail.lead')}</p>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>{t('packDetail.coverageTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <CoverageMeter label={t('rulebase.zones')} counts={pack.zones} testId="coverage-zones" />
          <CoverageMeter label={t('rulebase.rules')} counts={rules} testId="coverage-rules" />
        </CardContent>
      </Card>

      {/* The two sections below stand on the detail route. One loading line
          and one error box for both — they are one response. */}
      {detail.loading ? (
        <p className="text-[13px] text-muted" data-testid="pack-detail-loading">
          {t('common.loading')}
        </p>
      ) : detail.error ? (
        <div data-testid="pack-detail-error">
          <ErrorNote
            message={`${t('packDetail.loadError')}: ${detail.error}`}
            onRetry={detail.reload}
            retryLabel={t('common.retry')}
          />
        </div>
      ) : detail.data ? (
        <>
          <ZonesSection detail={detail.data} />
          <LayersSection detail={detail.data} packId={packId} />
        </>
      ) : null}

      <section aria-labelledby="pack-sources" className="flex flex-col gap-4">
        <h2 id="pack-sources" className="font-display text-[21px] font-semibold">
          {t('rulebase.sources')}
        </h2>
        {sources.length === 0 ? (
          <p className="text-[13px] text-muted">{t('rulebase.noSources')}</p>
        ) : (
          <ul
            data-testid="pack-sources"
            className="flex flex-col divide-y divide-border rounded-md border border-border bg-white"
          >
            {sources.map((source) => (
              <li key={source.key} className="flex flex-col gap-1 p-4">
                <span className="flex items-start gap-2 text-[14px] text-ink">
                  <Icon icon={FileText} className="mt-1" />
                  <span>
                    {source.doc ?? source.key}
                    <span className="num ms-2 text-[12px] text-muted">
                      {source.version
                        ? `${t('report.asOf')} ${source.version}`
                        : t('report.sourceNoVersion')}
                    </span>
                  </span>
                </span>
                {source.version_caveat ? (
                  <p
                    data-testid="pack-source-caveat"
                    className="ms-6 text-[12px] text-signal-amber-text"
                  >
                    {source.version_caveat}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status — text first, colour second. Same treatment as the draft surfaces:
// `info` + shield for verified, `indicative` (dashed, grey) for everything else.
// An unknown status string is shown raw rather than mapped to something else.
// ---------------------------------------------------------------------------
function statusLabel(status: string | null | undefined, t: Translate): string {
  switch (status) {
    case 'verified':
    case 'unverified':
    case 'placeholder':
      return t(`verify.status.${status}`)
    default:
      return status ?? '—'
  }
}

function StatusBadge({ status }: { status: string | null | undefined }) {
  const { t } = useI18n()
  const verified = status === 'verified'
  return (
    <Badge
      variant={verified ? 'info' : 'indicative'}
      data-testid="status-badge"
      data-status={status ?? ''}
    >
      {verified ? <Icon icon={ShieldCheck} /> : null}
      {statusLabel(status, t)}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Zonen — one table row per zone row of the matrix.
// ---------------------------------------------------------------------------
function ZonesSection({ detail }: { detail: CommittedPackDetailBody }) {
  const { t } = useI18n()
  const zones = detail.zones ?? []
  const ratio = new Set(detail.ratio_parameters ?? [])
  const caption = t('packDetail.zonesCaption', { gemeinde: detail.gemeinde, count: zones.length })

  return (
    <section aria-labelledby="pack-zones" className="flex flex-col gap-4">
      <div>
        <h2 id="pack-zones" className="font-display text-[21px] font-semibold">
          {t('packDetail.zonesTitle')}
        </h2>
        <p className="mt-1 max-w-[70ch] text-[13px] text-muted">{t('packDetail.zonesLead')}</p>
      </div>
      {zones.length === 0 ? (
        <p className="text-[13px] text-muted" data-testid="pack-zones-empty">
          {t('packDetail.zonesEmpty')}
        </p>
      ) : (
        // Same keyboard/landmark treatment as the audit table: the scroll
        // container is focusable and named, `group` not `region`.
        <div
          className="overflow-x-auto rounded-md border border-border bg-white"
          tabIndex={0}
          role="group"
          aria-label={caption}
        >
          <table className="w-full min-w-[640px] border-collapse text-left text-[13px]">
            <caption className="sr-only">{caption}</caption>
            <thead>
              <tr className="border-b border-border text-[12px] tracking-wide text-muted uppercase">
                <th scope="col" className="px-4 py-2 font-medium">
                  {t('packDetail.zoneCode')}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t('packDetail.zoneStatus')}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t('packDetail.zoneParameters')}
                </th>
              </tr>
            </thead>
            <tbody data-testid="pack-zones">
              {zones.map((zone) => (
                <ZoneRow key={zone.code} zone={zone} ratio={ratio} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/** `0.4` for a Ziffer reads «0.4 (40 %)» — the two notations a BZO prints. */
function parameterText(
  key: string,
  value: CommittedZoneRow['parameters'][string],
  ratio: Set<string>,
  t: Translate,
): string {
  const base = formatValue(value, t('packDetail.zoneNull'), t('common.yes'), t('common.no'))
  if (typeof value === 'number' && ratio.has(key)) {
    return `${base} (${formatNumber(value * 100)} %)`
  }
  return base
}

function ZoneRow({ zone, ratio }: { zone: CommittedZoneRow; ratio: Set<string> }) {
  const { t } = useI18n()
  const verified = zone.status === 'verified'
  const parameters = Object.entries(zone.parameters)
  const heldBack = Object.entries(zone.unverified_values ?? {})

  return (
    <tr
      className={[
        'border-b border-border align-top last:border-b-0',
        verified ? '' : 'bg-surface-2',
      ].join(' ')}
      data-testid="zone-row"
      data-zone={zone.code}
      data-status={zone.status ?? ''}
    >
      <th scope="row" className="px-4 py-3 font-normal">
        <span className="num text-[14px] font-semibold text-ink">{zone.code}</span>
        {(zone.aliases ?? []).length > 0 ? (
          <span className="mt-0.5 block text-[12px] text-muted" data-testid="zone-aliases">
            {t('packDetail.zoneAliases', { aliases: (zone.aliases ?? []).join(', ') })}
          </span>
        ) : null}
        {zone.regime ? (
          <span className="mt-0.5 block text-[12px] text-muted">
            {t('packDetail.zoneRegime', { regime: zone.regime })}
          </span>
        ) : null}
      </th>
      <td className="px-4 py-3">
        <StatusBadge status={zone.status} />
        {zone.verified_on ? (
          <span className="num mt-1 block text-[12px] text-muted">
            {t('packDetail.zoneVerifiedOn', { date: zone.verified_on })}
          </span>
        ) : null}
      </td>
      <td className="px-4 py-3">
        {parameters.length === 0 ? (
          <span className="text-[12px] text-muted">{t('packDetail.zoneNoParameters')}</span>
        ) : (
          <dl className="flex flex-wrap gap-x-4 gap-y-1" data-testid="zone-parameters">
            {parameters.map(([key, value]) => (
              <div key={key} className="flex gap-1.5">
                <dt className="num text-[12px] text-muted">{key}</dt>
                <dd className="num text-[13px] text-ink" data-parameter={key}>
                  {parameterText(key, value, ratio, t)}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {heldBack.length > 0 ? (
          <p className="mt-2 text-[12px] text-muted" data-testid="zone-held-back">
            {t('packDetail.zoneHeldBack', {
              values: heldBack
                .map(([key, value]) => `${key} = ${parameterText(key, value, ratio, t)}`)
                .join(', '),
            })}
          </p>
        ) : null}
        {(zone.notes_de ?? []).map((note) => (
          <p key={note} className="mt-2 max-w-[70ch] text-[12px] text-muted">
            {note}
          </p>
        ))}
        {zone.verified_from ? (
          <blockquote
            className="mt-2 max-w-[70ch] border-s-2 border-border ps-3 text-[12px] text-muted"
            data-testid="zone-verified-from"
          >
            {t('packDetail.zoneVerifiedFrom', { source: zone.verified_from })}
          </blockquote>
        ) : null}
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Regeln nach Ebene — the engine's load order, every rule described.
// ---------------------------------------------------------------------------
function openRules(layer: CommittedLayerRules): number {
  return layer.rules.filter((rule) => rule.status !== 'verified').length
}

function LayersSection({ detail, packId }: { detail: CommittedPackDetailBody; packId: string }) {
  const { t } = useI18n()
  const layers = detail.layers ?? []

  return (
    <section aria-labelledby="pack-layers" className="flex flex-col gap-4">
      <div>
        <h2 id="pack-layers" className="font-display text-[21px] font-semibold">
          {t('packDetail.layersTitle')}
        </h2>
        <p className="mt-1 max-w-[70ch] text-[13px] text-muted">{t('packDetail.layersLead')}</p>
      </div>
      {layers.length === 0 ? (
        <p className="text-[13px] text-muted" data-testid="pack-layers-empty">
          {t('packDetail.layersEmpty')}
        </p>
      ) : (
        <ul data-testid="pack-layers" className="flex flex-col gap-6">
          {layers.map((layer) => {
            const open = openRules(layer)
            const headingId = `pack-layer-${layer.pack.replace(/[^a-z0-9]+/gi, '-')}`
            return (
              <li
                key={layer.pack}
                data-testid="pack-layer"
                data-layer={layer.pack}
                data-open={open}
                className="flex flex-col gap-3"
              >
                <h3
                  id={headingId}
                  className="flex flex-wrap items-center gap-2 text-[15px] font-semibold"
                >
                  <Icon icon={GitBranch} className="text-muted-2" />
                  <code className="num text-[13px] text-ink-2">{layer.pack}</code>
                  <span className="text-[12px] font-normal text-muted">
                    {layer.rules.length === 1
                      ? t('packDetail.layerRulesOne')
                      : t('packDetail.layerRules', { count: layer.rules.length })}
                  </span>
                  {open > 0 ? <OpenCountBadge count={open} /> : null}
                </h3>
                <ul aria-labelledby={headingId} className="flex flex-col gap-3">
                  {layer.rules.map((rule) => (
                    <RuleRow key={rule.id} rule={rule} />
                  ))}
                </ul>
              </li>
            )
          })}
        </ul>
      )}
      <p className="max-w-[70ch] text-[12px] text-muted" data-testid="pack-rule-ids-note">
        {t('packDetail.ruleIdsNote', { pack: packId })}
      </p>
    </section>
  )
}

function typeLabel(type: string | null | undefined, t: Translate): string {
  switch (type) {
    case 'computable':
    case 'required':
    case 'nachweis':
    case 'narrative':
      return t(`packDetail.ruleType.${type}`)
    default:
      return type ?? '—'
  }
}

function severityLabel(severity: string | null | undefined, t: Translate): string | null {
  switch (severity) {
    case 'red':
    case 'amber':
    case 'green':
      return t(`severity.${severity}`)
    default:
      return severity ?? null
  }
}

function appliesText(applies: CommittedRule['applies_to']): string | null {
  if (!applies) return null
  const entries = Object.entries(applies)
  if (entries.length === 0) return null
  return entries
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`)
    .join(' · ')
}

function sourceText(rule: CommittedRule, asOf: string): string | null {
  const source = rule.source
  if (!source) return null
  const parts = [source.doc, source.article].filter((part): part is string => !!part)
  if (source.version) parts.push(`${asOf} ${source.version}`)
  return parts.length > 0 ? parts.join(' · ') : null
}

function RuleRow({ rule }: { rule: CommittedRule }) {
  const { t } = useI18n()
  const verified = rule.status === 'verified'
  const title = rule.title_de ?? rule.note_de ?? null
  const severity = severityLabel(rule.severity, t)
  const applies = appliesText(rule.applies_to)
  const source = sourceText(rule, t('report.asOf'))
  const inputs = rule.inputs ?? []

  return (
    <li
      className={[
        'rounded-md border bg-white px-5 py-4',
        verified ? 'border-border' : 'border-dashed border-border-strong',
      ].join(' ')}
      data-testid="pack-rule"
      data-rule={rule.id}
      data-status={rule.status ?? ''}
    >
      <h4 className="m-0 flex flex-wrap items-center gap-2 text-[14px] font-semibold">
        <code className="num text-[13px] font-normal text-muted-2">{rule.id}</code>
        <StatusBadge status={rule.status} />
        <Badge variant="outline" data-testid="rule-type">
          {typeLabel(rule.type, t)}
        </Badge>
      </h4>
      {title ? <p className="mt-2 max-w-[80ch] text-[14px] text-ink">{title}</p> : null}
      {rule.title_de && rule.note_de ? (
        <p className="mt-1 max-w-[80ch] text-[13px] text-muted">{rule.note_de}</p>
      ) : null}
      {!verified ? (
        <p
          role="note"
          className="mt-2 max-w-[80ch] text-[12px] text-muted"
          data-testid="rule-unverified-note"
        >
          {t('packDetail.ruleUnverified')}
        </p>
      ) : null}

      <dl className="mt-3 grid gap-x-6 gap-y-1.5 text-[13px] sm:grid-cols-[max-content_1fr]">
        <dt className="text-muted">{t('packDetail.ruleChecks')}</dt>
        <dd>
          {rule.check_summary ? (
            <code
              className="num block break-words whitespace-pre-wrap text-[13px] text-ink"
              data-testid="rule-check"
            >
              {rule.check_summary}
            </code>
          ) : (
            <span className="text-muted" data-testid="rule-no-check">
              {t('packDetail.ruleNoCheck')}
            </span>
          )}
        </dd>
        {severity ? (
          <>
            <dt className="text-muted">{t('packDetail.ruleSeverity')}</dt>
            <dd className="text-ink" data-testid="rule-severity">
              {severity}
            </dd>
          </>
        ) : null}
        {inputs.length > 0 ? (
          <>
            <dt className="text-muted">{t('packDetail.ruleInputs')}</dt>
            <dd className="num text-ink">{inputs.join(', ')}</dd>
          </>
        ) : null}
        {applies ? (
          <>
            <dt className="text-muted">{t('packDetail.ruleAppliesTo')}</dt>
            <dd className="num text-ink">{applies}</dd>
          </>
        ) : null}
        {source ? (
          <>
            <dt className="text-muted">{t('report.source')}</dt>
            <dd className="text-ink" data-testid="rule-source">
              {source}
            </dd>
          </>
        ) : null}
        {rule.vorbehalt_de ? (
          <>
            <dt className="text-muted">{t('packDetail.ruleVorbehalt')}</dt>
            <dd className="text-ink">{rule.vorbehalt_de}</dd>
          </>
        ) : null}
        {rule.verify_note ? (
          <>
            <dt className="text-muted">{t('packDetail.ruleVerifyNote')}</dt>
            <dd className="text-ink" data-testid="rule-verify-note">
              {rule.verify_note}
            </dd>
          </>
        ) : null}
      </dl>
      {rule.verified_from ? (
        <blockquote className="mt-3 max-w-[80ch] border-s-2 border-border ps-3 text-[12px] text-muted">
          {t('packDetail.zoneVerifiedFrom', { source: rule.verified_from })}
        </blockquote>
      ) : null}
    </li>
  )
}
